import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

export const SocketProvider = ({ children }) => {
  const { token, user, isAuthenticated } = useAuth();
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const listenersRef = useRef({});

  // ─── Connect socket when authenticated ────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    socketRef.current = io(SOCKET_URL, {
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      transports: ['websocket', 'polling']
    });

    const socket = socketRef.current;

    socket.on('connect', () => {
      console.log('✅ Socket connected:', socket.id);
      setIsConnected(true);
    });

    socket.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.error('Socket connection error:', err.message);
      setIsConnected(false);
    });

    // ─── Presence Events ───────────────────────────────────────────
    socket.on('users:online-list', (userIds) => {
      setOnlineUsers(new Set(userIds));
    });

    socket.on('user:online', ({ userId }) => {
      setOnlineUsers(prev => new Set([...prev, userId]));
    });

    socket.on('user:offline', ({ userId }) => {
      setOnlineUsers(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('users:online-list');
      socket.off('user:online');
      socket.off('user:offline');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isAuthenticated, token]);

  // ─── Emit helpers ──────────────────────────────────────────────────────
  const sendMessage = useCallback((data, callback) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('message:send', data, callback);
      return true;
    }
    return false;
  }, []);

  const startTyping = useCallback((receiverId, conversationId) => {
    socketRef.current?.emit('typing:start', { receiverId, conversationId });
  }, []);

  const stopTyping = useCallback((receiverId, conversationId) => {
    socketRef.current?.emit('typing:stop', { receiverId, conversationId });
  }, []);

  const markMessagesRead = useCallback((conversationId, senderId) => {
    socketRef.current?.emit('messages:read', { conversationId, senderId });
  }, []);

  // ─── Subscribe / Unsubscribe ───────────────────────────────────────────
  const on = useCallback((event, handler) => {
    socketRef.current?.on(event, handler);
  }, []);

  const off = useCallback((event, handler) => {
    socketRef.current?.off(event, handler);
  }, []);

  const isUserOnline = useCallback((userId) => {
    return onlineUsers.has(userId?.toString());
  }, [onlineUsers]);

  return (
    <SocketContext.Provider value={{
      socket: socketRef.current,
      isConnected,
      onlineUsers,
      isUserOnline,
      sendMessage,
      startTyping,
      stopTyping,
      markMessagesRead,
      on,
      off
    }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within SocketProvider');
  return ctx;
};
